import * as React from "react";
import {createContext, type ReactNode, useContext, useEffect, useState} from "react";
import {authApi} from "@/lib/api.ts";
import { storageService, STORAGE_KEYS } from "@/lib/storage";

export type User = {
    email: string;
    roles: string[];
}

export type LoginRequest = {
    email: string;
    password: string;
}

export type LoginResponse = {
    token: string;
    roles: string[];
}

export type LoginResult =
    | { ok: true }
    | { ok: false, status: number, message: string };

export const TOKEN_KEY = STORAGE_KEYS.authToken;

const AuthContext = createContext<AuthContextType | null>(null)

function getStoredUser(): User | null {
    return storageService.getJson<User>(STORAGE_KEYS.authUser)
}

function setStoredUser(user: User | null) {
    if (user) {
        storageService.setJson(STORAGE_KEYS.authUser, user)
    } else {
        storageService.remove(STORAGE_KEYS.authUser)
    }
}

function getStoredToken() {
    return storageService.getString(STORAGE_KEYS.authToken)
}

function setStoredToken(token: string | null) {
    if (token) {
        storageService.setString(STORAGE_KEYS.authToken, token)
    } else {
        storageService.remove(STORAGE_KEYS.authToken)
    }
}


export type AuthContextType = {
    user: User | null;
    token: string | null;
    isAuthenticated: boolean;
    login: (input: LoginRequest) => Promise<LoginResult>;
    logout: () => void;
}

export function AuthProvider({children}: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(getStoredUser());
    const [token, setToken] = useState<string | null>(getStoredToken());
    const isAuthtenticated = !!token;

    useEffect(() => {
        setUser(getStoredUser());
        setToken(getStoredToken());
    }, [])

    const login = React.useCallback(async (input: LoginRequest): Promise<LoginResult> => {
        try {
            const response = await authApi.login(input);
            const data = response.data as LoginResponse;
            const usr: User = {
                email: input.email,
                roles: data.roles,
            };
            setUser(usr);
            setStoredUser(usr);
            setToken(data.token);
            setStoredToken(data.token);
            return {ok: true};
        } catch (error: any) {
            setStoredToken(null);
            setStoredUser(null);
            // If axios error
            const status = error?.response?.status ?? 500;
            const message = error?.response?.data?.message ?? error?.response?.data ?? "Something went wrong";
            if (status === 401) {
                logout();
                return {ok: false, status, message: "Invalid Credentials"};
            }
            return {ok: false, status, message};
        }
    }, []);


    const logout = React.useCallback(async () => {
        setToken(null)
        setUser(null)
        storageService.remove(STORAGE_KEYS.authToken)
        storageService.remove(STORAGE_KEYS.authUser)
    },[]);

    return (
        <AuthContext.Provider value={{user, token, isAuthenticated: isAuthtenticated, login, logout}}>
            {children}
        </AuthContext.Provider>);
}

export function useAuth(): AuthContextType {
    const ctx = useContext(AuthContext)
    if (!ctx) throw new Error("useAuth must be used inside <AuthProvider />")
    return ctx
}

